/**
 * Ad-click attribution (3 Oct 2026): the Google Ads click id and UTM tags a customer arrived with,
 * carried from the HOST page onto every enquiry so the charter and the CRM deal can say which
 * campaign produced the booking. Nothing here is visible to the customer.
 *
 * Contract — `config.attribution` passed to `boatWidget.init(config)`:
 *   { gclid?, wbraid?, gbraid?, utm_source?, utm_medium?, utm_campaign?, utm_term?, utm_content? }
 * A host may supply it itself; when it does not, shim.js builds it from the host page with
 * {@link resolveHostAttribution}. The widget runs in a cross-origin iframe and cannot read the host
 * URL, which is why the capture has to happen in the shim (or the host).
 *
 * Capture rules (host side): read the keys from the page URL; remember the FIRST touch in the host's
 * localStorage under `bb_attribution` for 90 days; a URL carrying a fresh click id replaces the
 * stored record; a URL with nothing uses the stored record. Every storage access is guarded — a
 * blocked or throwing localStorage just means no persistence, never a broken page.
 *
 * Shared by shim.js (host page), the booking widget (CustomerDetail's submit params) and the retail
 * site-enquiry form (site-enquiry-form-controller.ts). Keep it dependency-free: shim.js is meant
 * to stay tiny.
 */
export const ATTRIBUTION_KEYS = [
    "gclid",
    "wbraid",
    "gbraid",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
];
export const ATTRIBUTION_STORAGE_KEY = "bb_attribution";
export const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** Column width server-side (site_enquiries / charters). */
const MAX_VALUE_LENGTH = 255;
const CLICK_ID_KEYS = ["gclid", "wbraid", "gbraid"];
/** The request fields the booking endpoints and the retail form accept (see App\Support\AdClickAttribution). */
export const ATTRIBUTION_REQUEST_FIELDS = ["gclid", "utm_source", "utm_medium", "utm_campaign"];
function cleanValue(value) {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed.slice(0, MAX_VALUE_LENGTH);
}
/** Keep only known keys with usable string values; null when nothing is left. */
export function sanitiseAttribution(value) {
    if (!value || typeof value !== "object") {
        return null;
    }
    const source = value;
    const result = {};
    let found = false;
    for (const key of ATTRIBUTION_KEYS) {
        const cleaned = cleanValue(source[key]);
        if (cleaned !== null) {
            result[key] = cleaned;
            found = true;
        }
    }
    return found ? result : null;
}
/** Attribution keys present in a URL query string (`?gclid=...&utm_source=...`). */
export function readAttributionFromSearch(search) {
    if (!search) {
        return null;
    }
    let params;
    try {
        params = new URLSearchParams(search);
    }
    catch (_a) {
        return null;
    }
    const raw = {};
    for (const key of ATTRIBUTION_KEYS) {
        const value = params.get(key);
        if (value !== null) {
            raw[key] = value;
        }
    }
    return sanitiseAttribution(raw);
}
function hasClickId(attribution) {
    return !!attribution && CLICK_ID_KEYS.some((key) => !!attribution[key]);
}
function readStored(storage, now) {
    if (!storage) {
        return null;
    }
    try {
        const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (typeof (parsed === null || parsed === void 0 ? void 0 : parsed.t) !== "number" || now - parsed.t > ATTRIBUTION_TTL_MS || parsed.t > now) {
            return null;
        }
        return sanitiseAttribution(parsed.v);
    }
    catch (_a) {
        return null;
    }
}
function writeStored(storage, attribution, now) {
    if (!storage) {
        return;
    }
    try {
        storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify({ v: attribution, t: now }));
    }
    catch (_a) {
        // Quota, Safari private mode, blocked storage: attribution just will not persist.
    }
}
/**
 * The host page's attribution: the current URL's, remembered first-touch for 90 days.
 *
 * - URL carries a click id            -> store it (replacing any older record) and use it.
 * - URL has no click id, record valid -> use the stored first touch (UTM-only revisits never
 *                                        overwrite the ad click that brought the customer).
 * - URL has UTM only, nothing stored  -> store it and use it.
 * - nothing anywhere                  -> null.
 */
export function resolveHostAttribution(search, storage, now) {
    const current = readAttributionFromSearch(search);
    if (hasClickId(current)) {
        writeStored(storage, current, now);
        return current;
    }
    const stored = readStored(storage, now);
    if (stored) {
        return stored;
    }
    if (current) {
        writeStored(storage, current, now);
    }
    return current;
}
/** window.localStorage, or null when it is missing or its getter throws (sandboxed iframes, blocked cookies). */
export function safeLocalStorage() {
    try {
        const storage = typeof window !== "undefined" ? window.localStorage : undefined;
        return storage !== null && storage !== void 0 ? storage : null;
    }
    catch (_a) {
        return null;
    }
}
/** The current page's search string, or null outside a browser. */
export function currentSearch() {
    try {
        return typeof window !== "undefined" && window.location ? window.location.search : null;
    }
    catch (_a) {
        return null;
    }
}
/**
 * The fields posted to the booking endpoints: `gclid` (gclid, else wbraid, else gbraid) and the
 * three UTM fields the charter stores. Empty when there is no attribution.
 */
export function attributionRequestParams(value) {
    var _a, _b;
    const attribution = sanitiseAttribution(value);
    if (!attribution) {
        return {};
    }
    const params = {};
    const clickId = (_b = (_a = attribution.gclid) !== null && _a !== void 0 ? _a : attribution.wbraid) !== null && _b !== void 0 ? _b : attribution.gbraid;
    if (clickId) {
        params.gclid = clickId;
    }
    if (attribution.utm_source)
        params.utm_source = attribution.utm_source;
    if (attribution.utm_medium)
        params.utm_medium = attribution.utm_medium;
    if (attribution.utm_campaign)
        params.utm_campaign = attribution.utm_campaign;
    return params;
}
/**
 * Capture the visitor's attribution from the CURRENT page and remember it (first touch, 90 days).
 *
 * Why this exists (6 Oct 2026): the widget shim only runs on pages that embed the widget (the boat
 * page). A paid click lands on a listing page (/boat-hire/boat-hire-sydney?gclid=...), and the boat
 * cards link to /boat/{slug} WITHOUT the query string, so by the time the shim ran the click id was
 * gone and nothing had been stored: 0 of 325 enquiries carried a gclid. The retail site's global
 * bundle (theme/frontend/index.ts) calls this on every page so the landing page itself writes
 * `bb_attribution`, which the shim and the retail enquiry form then read on later pages.
 *
 * Never throws; returns the resolved attribution (or null).
 */
export function captureHostAttribution(search = currentSearch(), storage = safeLocalStorage(), now = Date.now()) {
    try {
        return resolveHostAttribution(search, storage, now);
    }
    catch (_a) {
        return null;
    }
}
